import { useNavigate } from 'react-router-dom';
import { BellRing, CalendarPlus, Zap } from 'lucide-react';
import { useLaunchTimer } from '../hooks/useLaunchTimer';
import NotifyMeForm from './NotifyMeForm';

const pad = (n) => String(n).padStart(2, '0');

function Digit({ value, label, size }) {
  const v = pad(value);
  const box = size === 'sm' ? 'w-9 h-10 text-lg' : size === 'lg' ? 'w-16 h-[72px] text-4xl md:w-[76px] md:h-[84px] md:text-5xl' : 'w-12 h-14 text-2xl md:w-14 md:h-16 md:text-3xl';
  return (
    <div className="flex flex-col items-center gap-1.5" data-testid={`countdown-${label.toLowerCase()}`}>
      <div className={`relative ${box} rounded-lg bg-[#111214] border border-white/10 overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_10px_30px_rgba(0,0,0,0.35)]`}>
        <span className="absolute inset-x-0 top-1/2 h-px bg-black/60" />
        <span key={v} className="launch-digit absolute inset-0 flex items-center justify-center font-mono font-bold text-white tabular-nums">
          {v}
        </span>
      </div>
      <span className={`font-mono uppercase tracking-[0.25em] text-neutral-500 ${size === 'sm' ? 'text-[8px]' : 'text-[10px]'}`}>{label}</span>
    </div>
  );
}

function Colon({ size }) {
  return <span className={`launch-colon font-mono font-bold text-[#E02128] ${size === 'sm' ? 'text-lg mt-1' : size === 'lg' ? 'text-4xl mt-3' : 'text-2xl mt-2'}`}>:</span>;
}

function Digits({ remaining, size = 'md' }) {
  return (
    <div className="flex items-start gap-1.5 md:gap-2" data-testid="countdown-digits">
      <Digit value={remaining.days} label="Days" size={size} />
      <Colon size={size} />
      <Digit value={remaining.hours} label="Hours" size={size} />
      <Colon size={size} />
      <Digit value={remaining.minutes} label="Mins" size={size} />
      <Colon size={size} />
      <Digit value={remaining.seconds} label="Secs" size={size} />
    </div>
  );
}

function downloadIcs(launchAt) {
  const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const end = new Date(launchAt.getTime() + 60 * 60 * 1000);
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//DYSSC//Launch//EN', 'BEGIN:VEVENT',
    `UID:dyssc-hexcore-launch@dyssc.in`, `DTSTAMP:${fmt(new Date())}`, `DTSTART:${fmt(launchAt)}`, `DTEND:${fmt(end)}`,
    'SUMMARY:DYSSC HexCore Pro — Sale goes live', 'DESCRIPTION:Grab your HexCore Pro at dyssc.in the moment the sale opens.',
    'URL:https://dyssc.in/product/hexcore', 'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
  const a = document.createElement('a');
  a.href = url; a.download = 'dyssc-hexcore-launch.ics'; a.click();
  URL.revokeObjectURL(url);
}

const istLabel = (d) => d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) + ' IST';

export function LaunchedBanner({ slug = 'hexcore', compact = false }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-xl border border-[#E02128]/30 bg-[#E02128]/5 p-4" data-testid="launch-live-stocking">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E02128] opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#E02128]" />
        </span>
        <p className="font-heading font-bold text-[#111214] uppercase tracking-wider text-sm">Sale is Live — stocking now</p>
      </div>
      <p className="text-xs text-neutral-500 mt-1">First units are being loaded. Leave your number and we'll ping you the second Add to Cart opens.</p>
      {compact ? (
        <button onClick={() => navigate(`/notify/${slug}`)} className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#111214] text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#E02128] transition-colors" data-testid="launch-live-notify-btn">
          <BellRing className="w-3.5 h-3.5" /> Notify Me
        </button>
      ) : (
        <div className="mt-4"><NotifyMeForm slug={slug} /></div>
      )}
    </div>
  );
}

export function LaunchCountdown({ variant = 'detail', dark = false }) {
  const t = useLaunchTimer();
  if (!t.counting) return null;

  if (variant === 'card') {
    return (
      <div className="w-full rounded-xl border border-neutral-200 bg-neutral-50 p-3" data-testid="launch-countdown-card">
        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#E02128] font-bold flex items-center gap-1.5">
          <Zap className="w-3 h-3" /> {t.headline}
        </p>
        <div className="mt-2"><Digits remaining={t.remaining} size="sm" /></div>
      </div>
    );
  }

  const size = variant === 'showcase' ? 'md' : 'lg';
  return (
    <div
      className={`launch-panel relative overflow-hidden rounded-2xl border p-5 md:p-6 ${dark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-neutral-200 shadow-[0_20px_50px_rgba(0,0,0,0.08)]'}`}
      data-testid={`launch-countdown-${variant}`}
    >
      <div className="launch-glow absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#E02128]/20 blur-3xl pointer-events-none" />
      <div className="relative">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E02128] font-bold flex items-center gap-2" data-testid="launch-headline">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E02128] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E02128]" />
          </span>
          {t.headline}
        </p>
        <div className="mt-4"><Digits remaining={t.remaining} size={size} /></div>
        <p className={`mt-4 text-xs ${dark ? 'text-neutral-400' : 'text-neutral-500'}`} data-testid="launch-time-label">
          Doors open <span className={`font-semibold ${dark ? 'text-white' : 'text-[#111214]'}`}>{istLabel(t.launchAt)}</span>. Limited first batch.
        </p>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => downloadIcs(t.launchAt)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E02128] hover:bg-[#B81A20] text-white font-bold uppercase tracking-[0.15em] text-xs transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] shadow-[0_12px_30px_rgba(224,33,40,0.28)]"
            data-testid="launch-calendar-btn"
          >
            <CalendarPlus className="w-4 h-4" /> Add to calendar
          </button>
        </div>
      </div>
    </div>
  );
}
